import { useCallback } from 'react';
import { ApolloError } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  ErpFundingBoardDocument,
  ErpSaveFundingIntakeMutationVariables,
  FundingOperatorInput,
  useErpChangeCandidateStatusMutation,
  useErpDeleteFundingIntakeMutation,
  useErpSaveFundingIntakeMutation,
  useErpSaveFundingOperatorMutation,
  useErpLinkCandidateToUserMutation,
  useErpResolveGeoAliasMutation,
  useErpSaveFundingCandidateMutation,
  useErpSetCandidateOperatorMutation,
  useErpUnlinkCandidateFromUserMutation,
} from 'core/graphql';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { useToasts } from '../Toast/useToasts';

interface IMutationResult {
  ok: boolean;
  message?: string;
  /** Id созданной строки — нужен вызывающему, чтобы открыть её карточку. */
  id?: string;
}

type ErrList =
  | Array<{ message?: string | null; code?: string | null } | null>
  | null
  | undefined;

const errText = (errors: ErrList): string | undefined =>
  errors
    ?.map((e) => e?.message || e?.code || null)
    .filter(Boolean)
    .join('\n') || undefined;

/**
 * ТЗ 0024 — запись в воронку.
 *
 * Раньше хук возвращал `{ok, message}`, а вызывающий его выбрасывал: смена
 * статуса молча откатывалась, и менеджер считал, что сохранилось. Теперь тост
 * поднимается ВНУТРИ хука — уронить его на пол больше нельзя.
 *
 * Два разных класса отказа, которые легко перепутать:
 *
 * * бизнес-ошибка приходит в `customErrors` (OutputMixin), промис при этом
 *   успешен;
 * * отказ прав — это `PermissionDenied`, он попадает в GraphQL `errors`, и при
 *   дефолтном `errorPolicy: 'none'` промис ОТКЛОНЯЕТСЯ. Без try/catch это
 *   необработанный reject в консоли и ноль обратной связи — ровно то, что
 *   получал FinanceManager.
 *
 * `refetchQueries` на доску: бейджи в таб-баре считает сервер, и без этого
 * они врали бы до перезагрузки страницы.
 */
export const useFundingMutations = (onDone?: () => void) => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  const [
    changeStatus,
    { loading: statusLoading },
  ] = useErpChangeCandidateStatusMutation();
  const [
    setOperator,
    { loading: operatorLoading },
  ] = useErpSetCandidateOperatorMutation();
  const [
    linkUser,
    { loading: linkLoading },
  ] = useErpLinkCandidateToUserMutation();
  const [
    unlinkUser,
    { loading: unlinkLoading },
  ] = useErpUnlinkCandidateFromUserMutation();
  const [
    resolveAlias,
    { loading: aliasLoading },
  ] = useErpResolveGeoAliasMutation();
  const [
    saveCandidate,
    { loading: saveLoading },
  ] = useErpSaveFundingCandidateMutation();
  const [
    saveOperator,
    { loading: operatorSaveLoading },
  ] = useErpSaveFundingOperatorMutation();
  const [
    saveIntake,
    { loading: intakeSaveLoading },
  ] = useErpSaveFundingIntakeMutation();
  const [
    deleteIntake,
    { loading: intakeDeleteLoading },
  ] = useErpDeleteFundingIntakeMutation();
  const notify = useCallback(
    (type: ToastTypes, title: string, text?: string) => {
      addToast({
        data: { type, title, text },
        type: GlobalToastTypeNames.Dofinansowanie,
      });
    },
    [addToast],
  );

  const settle = useCallback(
    (
      payload:
        | { success?: boolean | null; customErrors?: ErrList }
        | null
        | undefined,
      successKey: string,
    ): IMutationResult => {
      if (payload?.success) {
        notify(ToastTypes.success, t(successKey));
        onDone?.();
        return { ok: true };
      }
      const message = errText(payload?.customErrors);
      notify(ToastTypes.error, t('pages.dofinansowanie.toast.failed'), message);
      return { ok: false, message };
    },
    [notify, onDone, t],
  );

  const fail = useCallback(
    (error: unknown): IMutationResult => {
      const graphQLMessage = (error as ApolloError)?.graphQLErrors?.[0]
        ?.message;
      // Текст `permissions_checker` — единственный признак отказа прав,
      // который доезжает до клиента.
      const forbidden = Boolean(graphQLMessage?.includes('permission'));
      const title = forbidden
        ? t('pages.dofinansowanie.errors.forbidden')
        : t('pages.dofinansowanie.toast.failed');
      notify(ToastTypes.error, title, forbidden ? undefined : graphQLMessage);
      return { ok: false, message: graphQLMessage };
    },
    [notify, t],
  );

  // `fundingBoard` пересчитывает бейджи и счётчики колонок. Но карточки на
  // доске приходят ПЯТЬЮ независимыми запросами `fundingCandidates` (по
  // колонке), и без их обновления карточка после переноса оставалась в
  // прежней колонке, пока цифра в шапке уже менялась. `refetchQueries` по
  // имени операции перезапрашивает все активные её варианты разом.
  const boardRefetch = [
    { query: ErpFundingBoardDocument },
    'ErpFundingCandidates',
    // Счётчик «Показано X–Y из N» — отдельный запрос под теми же фильтрами;
    // без него после смены статуса «из N» и блокировка кнопки «дальше»
    // оставались от прежней выборки.
    'ErpFundingCandidatesTotal',
  ];

  // Набор живёт сразу на ЧЕТЫРЁХ экранах: вкладка «Наборы», карточка
  // оператора, карточка самого набора и строка в реестре операторов —
  // «Новый набор — посмотреть» там ВЫЧИСЛЯЕТСЯ из списка наборов. Список,
  // обновляющий не всё, оставляет остальные врать; реестра здесь и не
  // хватало, из-за чего после удаления набора его чип висел до перезагрузки.
  // Оператор живёт в реестре и в своей карточке; форма правки уехала на
  // уровень страницы и до `refetch` вкладки больше не дотягивается — да и не
  // должна: обновление принадлежит мутации, а не тому, кто её позвал.
  const operatorRefetch = ['ErpFundingOperators', 'ErpFundingOperator'];

  const intakeRefetch = [
    'ErpFundingIntakes',
    'ErpFundingIntakesTotal',
    'ErpFundingOperator',
    'ErpFundingOperators',
    'ErpFundingIntake',
  ];

  return {
    loading:
      statusLoading ||
      operatorLoading ||
      linkLoading ||
      unlinkLoading ||
      aliasLoading ||
      saveLoading ||
      operatorSaveLoading ||
      intakeSaveLoading ||
      intakeDeleteLoading,

    changeCandidateStatus: useCallback(
      async (candidateId: string, status: string, note?: string) => {
        try {
          const { data } = await changeStatus({
            variables: { candidateId, status, note },
            refetchQueries: boardRefetch,
          });
          return settle(
            data?.changeCandidateStatus,
            'pages.dofinansowanie.toast.statusChanged',
          );
        } catch (error) {
          return fail(error);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [changeStatus, settle, fail],
    ),

    setCandidateOperator: useCallback(
      async (candidateId: string, operatorId: string | null, note?: string) => {
        try {
          const { data } = await setOperator({
            variables: { candidateId, operatorId, note },
          });
          return settle(
            data?.setCandidateOperator,
            operatorId
              ? 'pages.dofinansowanie.toast.operatorSet'
              : 'pages.dofinansowanie.toast.operatorCleared',
          );
        } catch (error) {
          return fail(error);
        }
      },
      [setOperator, settle, fail],
    ),

    linkCandidateToUser: useCallback(
      async (candidateId: string, userId: string) => {
        try {
          const { data } = await linkUser({
            variables: { candidateId, userId },
            refetchQueries: boardRefetch,
          });
          return settle(
            data?.linkCandidateToUser,
            'pages.dofinansowanie.toast.linked',
          );
        } catch (error) {
          return fail(error);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [linkUser, settle, fail],
    ),

    unlinkCandidateFromUser: useCallback(
      async (candidateId: string) => {
        try {
          const { data } = await unlinkUser({
            variables: { candidateId },
            refetchQueries: boardRefetch,
          });
          return settle(
            data?.unlinkCandidateFromUser,
            'pages.dofinansowanie.toast.unlinked',
          );
        } catch (error) {
          return fail(error);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [unlinkUser, settle, fail],
    ),

    resolveGeoAlias: useCallback(
      async (id: string, powiatId: string | null, ignore = false) => {
        try {
          const { data } = await resolveAlias({
            variables: { id, powiatId, ignore },
            refetchQueries: boardRefetch,
          });
          return settle(
            data?.resolveGeoAlias,
            'pages.dofinansowanie.toast.geoResolved',
          );
        } catch (error) {
          return fail(error);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [resolveAlias, settle, fail],
    ),

    saveFundingCandidate: useCallback(
      // `id: null` — СОЗДАНИЕ. Тип был `string`, из-за чего создать кандидата
      // этим хуком было нельзя даже из кода, хотя мутация — upsert и `$id`
      // в документе уже nullable.
      async (id: string | null, input: Record<string, unknown>) => {
        try {
          const { data } = await saveCandidate({ variables: { id, input } });
          return settle(
            data?.saveFundingCandidate,
            'pages.dofinansowanie.toast.saved',
          );
        } catch (error) {
          return fail(error);
        }
      },
      [saveCandidate, settle, fail],
    ),

    saveFundingOperator: useCallback(
      async (id: string | null, input: FundingOperatorInput) => {
        try {
          const { data } = await saveOperator({
            variables: { id, input },
            refetchQueries: operatorRefetch,
          });
          return settle(
            data?.saveFundingOperator,
            'pages.dofinansowanie.toast.saved',
          );
        } catch (error) {
          return fail(error);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [saveOperator, settle, fail],
    ),

    saveFundingIntake: useCallback(
      async (input: ErpSaveFundingIntakeMutationVariables) => {
        try {
          const { data } = await saveIntake({
            variables: input,
            refetchQueries: intakeRefetch,
          });
          return {
            ...settle(
              data?.saveFundingIntake,
              'pages.dofinansowanie.toast.saved',
            ),
            id: data?.saveFundingIntake?.intake?.id,
          };
        } catch (error) {
          return fail(error);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [saveIntake, settle, fail],
    ),

    deleteFundingIntake: useCallback(
      async (id: string) => {
        try {
          const { data } = await deleteIntake({
            variables: { id },
            refetchQueries: intakeRefetch,
          });
          return settle(
            data?.deleteFundingIntake,
            'pages.dofinansowanie.toast.intakeDeleted',
          );
        } catch (error) {
          return fail(error);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [deleteIntake, settle, fail],
    ),
  };
};
